import * as React from "react";
import { cn } from "../cn";

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  /** Copy block, max ~24 words. Explains the next action, never just "No data". */
  body: string;
  cta?: React.ReactNode;
  /** Optional secondary action ("learn more") beneath the primary CTA. */
  secondary?: React.ReactNode;
  /** subtle/accent: dashed hero box. inline: left-aligned, no box (for empty table bodies). */
  variant?: "subtle" | "accent" | "inline";
  className?: string;
}

export function EmptyState({ icon, title, body, cta, secondary, variant = "subtle", className }: EmptyStateProps) {
  if (variant === "inline") {
    return (
      <div className={cn("flex flex-col items-start gap-1 px-1 py-6", className)}>
        <p className="text-sm font-medium text-ink">{title}</p>
        <p className="max-w-prose text-sm text-mute">{body}</p>
        {cta && <div className="mt-2">{cta}</div>}
      </div>
    );
  }
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-lg border border-dashed px-6 py-12 text-center",
        variant === "accent" ? "border-brand/40 bg-brandSoft" : "border-line bg-subtle",
        className,
      )}
    >
      {icon && (
        <div className={cn("mb-3 flex h-10 w-10 items-center justify-center rounded-md", variant === "accent" ? "bg-brandSoft text-brand" : "bg-card text-faint")}>
          {icon}
        </div>
      )}
      <p className="text-base font-semibold text-ink">{title}</p>
      <p className="mt-1 max-w-md text-sm text-mute">{body}</p>
      {cta && <div className="mt-4">{cta}</div>}
      {secondary && <div className="mt-2">{secondary}</div>}
    </div>
  );
}
